import { useState, useMemo } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useSendEtterrekvirering,
  useRequisitionBiometricsList,
} from "@/hooks/api/useLabVisits";
import type { Biometric } from "@/lib/api_client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MaskedText } from "@/components/masked-text";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function EtterrekvireringModal({
  open,
  onOpenChange,
  requisitionId,
  userName,
  allBiometrics,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requisitionId: string;
  userName?: string;
  allBiometrics: Biometric[];
}) {
  const sendEtterrekvirering = useSendEtterrekvirering();
  const [cookieKey, setCookieKey] = useState("");
  const [selectedBiomarkers, setSelectedBiomarkers] = useState<Set<string>>(
    new Set(),
  );
  const [search, setSearch] = useState("");

  const { data: reqBiometrics = [] } =
    useRequisitionBiometricsList(requisitionId);

  const existingBiometricIds = useMemo(
    () =>
      new Set(
        (reqBiometrics as { biometricId: string }[]).map(
          (rb) => rb.biometricId,
        ),
      ),
    [reqBiometrics],
  );

  // Already-requisitioned biometrics (read-only display)
  const existingBiometrics = useMemo(() => {
    return allBiometrics
      .filter((b) => existingBiometricIds.has(b.id))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb"));
  }, [allBiometrics, existingBiometricIds]);

  // Available biomarkers = all excluding already requisitioned
  const availableBiometrics = useMemo(() => {
    return allBiometrics
      .filter((b) => !existingBiometricIds.has(b.id))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb"));
  }, [allBiometrics, existingBiometricIds]);

  const filteredBiometrics = useMemo(() => {
    if (!search.trim()) return availableBiometrics;
    const q = search.toLowerCase();
    return availableBiometrics.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.shortName?.toLowerCase().includes(q) ||
        b.furstName?.toLowerCase().includes(q),
    );
  }, [availableBiometrics, search]);

  const toggleBiomarker = (id: string) => {
    const next = new Set(selectedBiomarkers);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedBiomarkers(next);
  };

  const handleSubmit = async () => {
    if (!cookieKey.trim()) {
      toast.error("Vennligst skriv inn FURST cookie");
      return;
    }
    if (selectedBiomarkers.size === 0) {
      toast.error("Velg minst en biomarkør");
      return;
    }

    try {
      await sendEtterrekvirering.mutateAsync({
        requisitionId,
        furstCookie: cookieKey,
        biometricIds: Array.from(selectedBiomarkers),
      });
      toast.success("Tillegsrekvirering sendt");
      onOpenChange(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Ukjent feil";
      toast.error(`Feil: ${message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tillegsrekvirering</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {userName && (
            <p className="text-sm text-admin-text-secondary">
              Legg til nye biomarkører for <MaskedText value={userName} />
            </p>
          )}

          {/* Cookie input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-admin-text-primary">
              FURST Cookie
            </label>
            <Textarea
              value={cookieKey}
              onChange={(e) => setCookieKey(e.target.value)}
              placeholder="Lim inn FURST cookie her..."
              disabled={sendEtterrekvirering.isPending}
              className="min-h-[60px]"
            />
          </div>

          {/* Already requisitioned biomarkers */}
          {existingBiometrics.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-admin-text-primary">
                Allerede rekvirert ({existingBiometrics.length})
              </label>
              <div className="flex flex-wrap gap-1.5">
                {existingBiometrics.map((b) => (
                  <span
                    key={b.id}
                    className="inline-flex items-center rounded-md bg-admin-card px-2 py-1 text-xs text-admin-text-secondary border border-admin-card-border"
                  >
                    {b.name}
                    {b.shortName && (
                      <span className="ml-1 text-admin-text-subtle">
                        ({b.shortName})
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Biomarker selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-admin-text-primary">
              Velg biomarkører ({selectedBiomarkers.size} valgt)
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søk biomarkører..."
              className="w-full rounded-md border border-admin-card-border bg-admin-bg px-3 py-2 text-sm text-admin-text-primary placeholder:text-admin-text-subtle focus:outline-none focus:ring-1 focus:ring-admin-primary"
            />
            <div className="max-h-[300px] overflow-y-auto space-y-0.5 border border-admin-card-border rounded-md p-2">
              {filteredBiometrics.map((b) => (
                <label
                  key={b.id}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors ${
                    selectedBiomarkers.has(b.id)
                      ? "bg-admin-primary/10 text-admin-text-primary"
                      : "hover:bg-admin-card/50 text-admin-text-secondary"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedBiomarkers.has(b.id)}
                    onChange={() => toggleBiomarker(b.id)}
                    disabled={sendEtterrekvirering.isPending}
                    className="rounded border-admin-card-border"
                  />
                  <span>{b.name}</span>
                  {b.shortName && (
                    <span className="text-xs text-admin-text-subtle">
                      ({b.shortName})
                    </span>
                  )}
                  {b.furstId && (
                    <span className="ml-1 text-admin-text-subtle">
                      (Furst ID: {b.furstId})
                    </span>
                  )}
                </label>
              ))}
              {filteredBiometrics.length === 0 && (
                <p className="text-sm text-admin-text-subtle text-center py-4">
                  {search.trim()
                    ? "Ingen treff"
                    : "Ingen tilgjengelige biomarkører"}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-admin-card-border">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={sendEtterrekvirering.isPending}
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                sendEtterrekvirering.isPending ||
                !cookieKey.trim() ||
                selectedBiomarkers.size === 0
              }
            >
              {sendEtterrekvirering.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Sender...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Send tillegsrekvirering
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
